import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Globe2, Search, RefreshCw, Loader2, Shield, UserCheck,
  UserPlus, CheckCircle2, Clock3, Mail, X, AlertTriangle, User, Send,
  History, UserPlus2, Clock, Inbox, Trash2
} from 'lucide-react';
import { apiClient } from '../lib/apiClient';
import { toast } from 'sonner';
import AdminSidebar from '../components/AdminSidebar';
import UltraAdminTopbar from '../components/UltraAdminTopbar';
import PageIntro from '../components/PageIntro';
import { getPageIntro } from '../lib/pageIntros';
import DelegateEmailCompose from '../components/DelegateEmailCompose';
import { useLanguage } from '../contexts/LanguageContext';

// ── Country-code → Unicode flag emoji (🇫🇷 etc.) ────────────────────────────
// Works for every ISO-3166-alpha-2 code. Adds 0x1F1A5 to each ASCII letter.
const codeToFlag = (code) => {
  if (!code || code.length !== 2) return '🏳️';
  const up = code.toUpperCase();
  return String.fromCodePoint(
    0x1F1E6 + (up.charCodeAt(0) - 65),
    0x1F1E6 + (up.charCodeAt(1) - 65),
  );
};

// ── Status meta (badge color + icon + label) ─────────────────────────────────
const STATUS_META = {
  delegate_registered: {
    label_tr: 'Delege Kayıtlı',
    label_en: 'Delegate Registered',
    icon: CheckCircle2,
    chip: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    dot:  'bg-emerald-500',
  },
  delegate_waiting: {
    label_tr: 'Delege Bekleniyor',
    label_en: 'Delegate Waiting',
    icon: Clock3,
    chip: 'bg-amber-100 text-amber-700 border-amber-200',
    dot:  'bg-amber-500',
  },
  self_register_active: {
    label_tr: 'Self-Register Aktif',
    label_en: 'Self-Register Active',
    icon: UserCheck,
    chip: 'bg-sky-100 text-sky-700 border-sky-200',
    dot:  'bg-sky-500',
  },
  self_register_pending: {
    label_tr: 'Başvuru Bekliyor',
    label_en: 'Pending Application',
    icon: UserPlus,
    chip: 'bg-gray-100 text-gray-600 border-gray-200',
    dot:  'bg-gray-400',
  },
};

// ── Summary Card ─────────────────────────────────────────────────────────────
const SummaryCard = ({ icon: Icon, label, value, tint = 'emerald' }) => {
  const tints = {
    emerald: 'from-emerald-500 to-teal-600',
    amber:   'from-amber-500 to-orange-600',
    sky:     'from-sky-500 to-indigo-600',
    slate:   'from-slate-500 to-gray-600',
  };
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{label}</p>
          <p className="text-3xl font-extrabold text-gray-900 mt-1">{value}</p>
        </div>
        <div className={`h-11 w-11 rounded-xl bg-gradient-to-br ${tints[tint]} flex items-center justify-center shrink-0`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
      </div>
    </div>
  );
};

// ── Country Card ─────────────────────────────────────────────────────────────
const CountryCard = ({ row, onClick, tr }) => {
  const meta = STATUS_META[row.status] || STATUS_META.self_register_pending;
  const StatusIcon = meta.icon;
  const primaryDelegate = (row.unfccc_emails || [])[0];
  const extraCount = Math.max(0, (row.unfccc_emails?.length || 0) - 1);
  return (
    <button
      type="button"
      data-testid={`delegate-card-${row.country_code}`}
      onClick={onClick}
      className="group text-left bg-white rounded-2xl p-4 shadow-sm border border-gray-100 hover:border-emerald-300 hover:shadow-md transition-all"
    >
      <div className="flex items-start gap-3">
        <div
          className="h-11 w-11 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center shrink-0 text-[28px] leading-none"
          title={row.country_code}
        >
          <span className="drop-shadow-sm">{codeToFlag(row.country_code)}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-bold text-gray-900 truncate text-sm">
              {tr ? row.country_tr : row.country_en}
            </h3>
            {row.unfccc_locked && (
              <Shield className="h-3.5 w-3.5 text-emerald-600 shrink-0" title={tr ? 'UNFCCC kilidi aktif' : 'UNFCCC lock active'} />
            )}
          </div>
          <p className="text-[11px] text-gray-400 mt-0.5">
            {tr ? row.country_en : row.country_tr}
            <span className="text-gray-300"> · {row.country_code}</span>
          </p>
        </div>
      </div>

      {/* Delegate name + email on the card */}
      {primaryDelegate && (
        <div className="mt-3 bg-gray-50 rounded-xl p-2.5 space-y-1">
          {primaryDelegate.name && (
            <div className="flex items-center gap-1.5 text-[12px] text-gray-800 font-semibold truncate">
              <User className="h-3 w-3 text-gray-400 shrink-0" />
              <span className="truncate">{primaryDelegate.name}</span>
            </div>
          )}
          <div className="flex items-center gap-1.5 text-[11px] text-gray-500 truncate">
            <Mail className="h-3 w-3 text-gray-400 shrink-0" />
            <span className="truncate">{primaryDelegate.email}</span>
          </div>
          {extraCount > 0 && (
            <p className="text-[10px] text-gray-400 pt-0.5">
              +{extraCount} {tr ? 'delege daha' : 'more delegate(s)'}
            </p>
          )}
        </div>
      )}

      <div className={`mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-full border ${meta.chip}`}>
        <StatusIcon className="h-3 w-3" />
        {tr ? meta.label_tr : meta.label_en}
      </div>

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-50 text-[11px]">
        <div className="flex items-center gap-3 text-gray-500">
          {row.unfccc_count > 0 && (
            <span className="flex items-center gap-1">
              <Shield className="h-3 w-3 text-emerald-500" />
              <strong className="text-gray-700">{row.unfccc_count}</strong> {tr ? 'delege' : 'delegate'}
            </span>
          )}
          {row.registered_count > 0 && (
            <span className="flex items-center gap-1">
              <UserCheck className="h-3 w-3 text-sky-500" />
              <strong className="text-gray-700">{row.registered_count}</strong> {tr ? 'kayıt' : 'user'}
            </span>
          )}
          {row.pending_count > 0 && (
            <span className="flex items-center gap-1">
              <Clock3 className="h-3 w-3 text-amber-500" />
              <strong className="text-gray-700">{row.pending_count}</strong> {tr ? 'bekliyor' : 'pending'}
            </span>
          )}
          {row.unfccc_count === 0 && row.registered_count === 0 && row.pending_count === 0 && (
            <span className="text-gray-400 italic">—</span>
          )}
        </div>
        <span className="text-gray-300 group-hover:text-emerald-500 transition-colors">→</span>
      </div>
    </button>
  );
};

// ── History Panel ────────────────────────────────────────────────────────────
const EVENT_META = {
  welcome_sent: { icon: Send,       tint: 'text-emerald-600 bg-emerald-50 border-emerald-200', label_tr: 'Mail gönderildi',  label_en: 'Email sent' },
  added:        { icon: UserPlus2,  tint: 'text-sky-600 bg-sky-50 border-sky-200',             label_tr: 'Delege eklendi',   label_en: 'Delegate added' },
  verified:     { icon: CheckCircle2,tint: 'text-teal-600 bg-teal-50 border-teal-200',          label_tr: 'Doğrulandı',       label_en: 'Verified' },
};
const codeToFlagLocal = (code) => {
  if (!code || code.length !== 2) return '🏳️';
  const up = code.toUpperCase();
  return String.fromCodePoint(0x1F1E6 + (up.charCodeAt(0) - 65), 0x1F1E6 + (up.charCodeAt(1) - 65));
};
const fmtRelative = (iso, tr) => {
  if (!iso) return '—';
  const t = new Date(iso);
  const diff = (Date.now() - t.getTime()) / 1000;
  if (diff < 60)         return tr ? 'az önce' : 'just now';
  if (diff < 3600)       return tr ? `${Math.floor(diff/60)} dk önce`   : `${Math.floor(diff/60)} min ago`;
  if (diff < 86400)      return tr ? `${Math.floor(diff/3600)} sa önce` : `${Math.floor(diff/3600)} hr ago`;
  if (diff < 86400 * 7)  return tr ? `${Math.floor(diff/86400)} gün önce` : `${Math.floor(diff/86400)} day(s) ago`;
  return t.toLocaleDateString(tr ? 'tr-TR' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const HistoryPanel = ({ history, loading, days, setDays, onRefresh, tr }) => {
  const events = history?.events || [];

  // Tally: sent_tr / sent_en / added / verified
  const tally = events.reduce((acc, e) => {
    if (e.type === 'welcome_sent') {
      if (e.language === 'tr') acc.sent_tr++;
      else                     acc.sent_en++;
    } else if (e.type === 'added')    acc.added++;
    else if (e.type === 'verified')   acc.verified++;
    return acc;
  }, { sent_tr: 0, sent_en: 0, added: 0, verified: 0 });

  return (
    <div className="space-y-4">
      {/* Controls row */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-xl p-1">
          {[7, 30, 90].map(n => (
            <button
              key={n}
              data-testid={`history-days-${n}`}
              onClick={() => setDays(n)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${
                days === n ? 'bg-emerald-600 text-white' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tr ? `Son ${n} gün` : `Last ${n}d`}
            </button>
          ))}
        </div>
        <button
          data-testid="history-refresh"
          onClick={onRefresh}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-emerald-600 border border-gray-200 rounded-xl px-3 py-2 bg-white"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          {tr ? 'Yenile' : 'Refresh'}
        </button>
        <div className="ml-auto flex items-center gap-2 text-[11px] text-gray-500">
          <span className="px-2 py-1 rounded-lg bg-emerald-50 border border-emerald-200 font-semibold text-emerald-700">
            {tally.sent_en} EN
          </span>
          <span className="px-2 py-1 rounded-lg bg-red-50 border border-red-200 font-semibold text-red-700">
            {tally.sent_tr} TR
          </span>
          <span className="px-2 py-1 rounded-lg bg-sky-50 border border-sky-200 font-semibold text-sky-700">
            {tally.added} {tr ? 'eklendi' : 'added'}
          </span>
          <span className="px-2 py-1 rounded-lg bg-teal-50 border border-teal-200 font-semibold text-teal-700">
            {tally.verified} {tr ? 'doğrulandı' : 'verified'}
          </span>
        </div>
      </div>

      {/* Timeline */}
      {loading && !history ? (
        <div className="flex justify-center py-24">
          <Loader2 className="h-10 w-10 text-emerald-500 animate-spin" />
        </div>
      ) : events.length === 0 ? (
        <div className="bg-white rounded-2xl p-16 text-center shadow-sm border" data-testid="history-empty">
          <Inbox className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-400 font-medium">
            {tr ? 'Seçilen dönemde kayıt yok.' : 'No events in the selected period.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm divide-y divide-gray-50">
          {events.map((e, idx) => {
            const meta = EVENT_META[e.type] || EVENT_META.added;
            const Icon = meta.icon;
            return (
              <div
                key={`${e.type}-${e.email}-${e.at}-${idx}`}
                data-testid={`history-row-${idx}`}
                className="px-5 py-3.5 flex items-center gap-4"
              >
                <div className={`h-8 w-8 rounded-xl border flex items-center justify-center shrink-0 ${meta.tint}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-lg leading-none">{codeToFlagLocal(e.country_code)}</span>
                    <span className="font-semibold text-gray-800 truncate">{e.name || e.email}</span>
                    {e.name && e.name !== e.email && (
                      <>
                        <span className="text-gray-300 shrink-0">·</span>
                        <span className="text-gray-400 text-xs truncate">{e.email}</span>
                      </>
                    )}
                  </div>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    <span className={`font-semibold ${e.type === 'welcome_sent' ? 'text-emerald-700' : e.type === 'verified' ? 'text-teal-700' : 'text-sky-700'}`}>
                      {tr ? meta.label_tr : meta.label_en}
                    </span>
                    {e.language && (
                      <span className={`ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-md ${e.language === 'tr' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                        {e.language.toUpperCase()}
                      </span>
                    )}
                    {e.by && <span className="ml-2 text-gray-400">· {e.by}</span>}
                  </p>
                </div>
                <span className="text-xs text-gray-400 shrink-0 tabular-nums" title={e.at}>
                  {fmtRelative(e.at, tr)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Main Page ────────────────────────────────────────────────────────────────
export default function UltraAdminDelegateMap() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const tr = language === 'tr';

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [drill, setDrill] = useState(null);
  const [view, setView] = useState('map');        // 'map' | 'history'
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyDays, setHistoryDays] = useState(30);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeRecipients, setComposeRecipients] = useState([]);
  // Phase 86AT — Purge-country modal (Ultra Password gated). Removes UNFCCC
  // emails, invitations, and pending blue-zone registrations for the country
  // so a stale row disappears from the Delegate Map.
  const [purgeModal, setPurgeModal] = useState(null); // { country, password, loading, error }
  const currentUserEmail = 'mbucak@gmail.com'; // Ultra admin actor — backend overrides anyway

  const openCompose = (recipients) => {
    setComposeRecipients(recipients || []);
    setComposeOpen(true);
  };

  // Phase 86AT — Submit the purge. Sends both `country_code` and the shared
  // Ultra Password to the backend; on success the country drops from the map
  // and the drill modal closes.
  const handlePurgeCountry = async () => {
    const c = purgeModal?.country;
    const pw = (purgeModal?.password || '').trim();
    if (!c || !pw) return;
    setPurgeModal(prev => ({ ...prev, loading: true, error: '' }));
    try {
      const res = await apiClient.delete(`/ultra-admin/unfccc-delegate-map/by-country`, {
        params: { country_code: c.country_code, ultra_password: pw },
      });
      const d = res.data || {};
      toast.success(
        tr
          ? `Silindi: ${d.deleted_emails} delege, ${d.deleted_invitations} davet, ${d.deleted_pending_regs} bekleyen kayıt.`
          : `Removed: ${d.deleted_emails} delegates, ${d.deleted_invitations} invites, ${d.deleted_pending_regs} pending regs.`
      );
      setPurgeModal(null);
      setDrill(null);
      load();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setPurgeModal(prev => ({ ...prev, loading: false, error: typeof msg === 'string' ? msg : 'Error' }));
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get('/ultra-admin/unfccc-delegate-map');
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 401) navigate('/giris?tab=ultra');
      else toast.error(tr ? 'Delege haritası yüklenemedi.' : 'Failed to load delegate map.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // ── Load timeline history ────────────────────────────────────────────────
  const loadHistory = async (daysOverride = null) => {
    const days = daysOverride ?? historyDays;
    setHistoryLoading(true);
    try {
      const r = await apiClient.get(
        `/ultra-admin/unfccc/email-history?days=${days}`,
      );
      setHistory(r.data);
    } catch (e) {
      if (e?.response?.status === 401) navigate('/giris?tab=ultra');
      else toast.error(tr ? 'Geçmiş yüklenemedi.' : 'Failed to load history.');
    } finally {
      setHistoryLoading(false);
    }
  };
  useEffect(() => {
    if (view === 'history') loadHistory();
    // eslint-disable-next-line
  }, [view, historyDays]);

  const items = data?.items || [];
  const filtered = items
    .filter(it => filter === 'all' || it.status === filter)
    .filter(it => {
      if (!query) return true;
      const q = query.trim().toLowerCase();
      return (
        it.country_en.toLowerCase().includes(q) ||
        it.country_tr.toLowerCase().includes(q) ||
        it.country_code.toLowerCase().includes(q) ||
        (it.unfccc_emails || []).some(e => (e.email || '').toLowerCase().includes(q))
      );
    });

  const totals = data?.totals || {};

  return (
    <div className="flex min-h-screen bg-gray-50">
      <AdminSidebar />
      <div className="flex-1 overflow-auto">
        <UltraAdminTopbar
          title={tr ? 'Delege Haritası' : 'Delegate Map'}
          subtitle={tr ? 'Her ülkenin UNFCCC resmi delege durumu ve Blue Zone kayıt aşaması' : "Each country's UNFCCC delegate status and Blue Zone registration state"}
          icon={Globe2}
          onRefresh={load}
          rightExtra={
            <button
              data-testid="delegate-map-send-all"
              onClick={() => {
                const allDelegates = (data?.items || []).flatMap(it =>
                  (it.unfccc_emails || []).map(e => ({
                    email: e.email,
                    name:  e.name || '',
                    country_code: it.country_code,
                  })),
                );
                openCompose(allDelegates);
              }}
              disabled={!data?.totals?.total_unfccc_emails}
              className="flex items-center gap-2 text-sm bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-semibold rounded-xl px-3 py-2 transition-colors"
            >
              <Send className="h-4 w-4" />
              {tr ? 'Tüm delegelere gönder' : 'Send to all delegates'}
            </button>
          }
        />

        <div className="p-6 space-y-6">
          <PageIntro {...getPageIntro('delegate-map', language)} />
          {/* View toggle: Map / History */}
          <div className="inline-flex bg-white border border-gray-200 rounded-xl p-1 gap-1">
            <button
              data-testid="delegate-view-map"
              onClick={() => setView('map')}
              className={`px-4 py-1.5 text-sm font-semibold rounded-lg flex items-center gap-2 transition-colors ${
                view === 'map' ? 'bg-emerald-600 text-white' : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              <Globe2 className="h-4 w-4" />
              {tr ? 'Harita' : 'Map'}
            </button>
            <button
              data-testid="delegate-view-history"
              onClick={() => setView('history')}
              className={`px-4 py-1.5 text-sm font-semibold rounded-lg flex items-center gap-2 transition-colors ${
                view === 'history' ? 'bg-emerald-600 text-white' : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              <History className="h-4 w-4" />
              {tr ? 'Geçmiş' : 'History'}
            </button>
          </div>

          {view === 'history' && (
            <HistoryPanel
              history={history}
              loading={historyLoading}
              days={historyDays}
              setDays={setHistoryDays}
              onRefresh={() => loadHistory()}
              tr={tr}
            />
          )}

          {view === 'map' && (<>
          {/* Summary row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <SummaryCard
              icon={Shield}
              label={tr ? 'UNFCCC Kilitli Ülke' : 'UNFCCC-Locked'}
              value={totals.countries_with_unfccc_lock ?? 0}
              tint="emerald"
            />
            <SummaryCard
              icon={CheckCircle2}
              label={tr ? 'Delege Kayıtlı' : 'Delegate Registered'}
              value={totals.delegate_registered ?? 0}
              tint="emerald"
            />
            <SummaryCard
              icon={Clock3}
              label={tr ? 'Delege Bekleniyor' : 'Delegate Waiting'}
              value={totals.delegate_waiting ?? 0}
              tint="amber"
            />
            <SummaryCard
              icon={UserCheck}
              label={tr ? 'Self-Register Aktif' : 'Self-Registered'}
              value={totals.self_register_active ?? 0}
              tint="sky"
            />
          </div>

          {/* Filter + Search */}
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                data-testid="delegate-map-search"
                placeholder={tr ? 'Ülke veya delege maili ara…' : 'Search country or delegate email…'}
                value={query}
                onChange={e => setQuery(e.target.value)}
                className="w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400"
              />
            </div>
            <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-xl p-1">
              {[
                { k: 'all', label_tr: 'Tümü', label_en: 'All' },
                { k: 'delegate_waiting', label_tr: 'Bekliyor', label_en: 'Waiting' },
                { k: 'delegate_registered', label_tr: 'Kayıtlı', label_en: 'Registered' },
                { k: 'self_register_active', label_tr: 'Self', label_en: 'Self' },
              ].map(o => (
                <button
                  key={o.k}
                  data-testid={`delegate-filter-${o.k}`}
                  onClick={() => setFilter(o.k)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                    filter === o.k
                      ? 'bg-emerald-600 text-white'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {tr ? o.label_tr : o.label_en}
                </button>
              ))}
            </div>
          </div>

          {/* Grid */}
          {loading && !data ? (
            <div className="flex justify-center py-24">
              <Loader2 className="h-10 w-10 text-emerald-500 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="bg-white rounded-2xl p-16 text-center shadow-sm border">
              <Globe2 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-400 font-medium">
                {tr ? 'Eşleşen ülke bulunamadı.' : 'No countries match.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filtered.map(row => (
                <CountryCard
                  key={row.country_code}
                  row={row}
                  tr={tr}
                  onClick={() => setDrill(row)}
                />
              ))}
            </div>
          )}
          </>)}
        </div>
      </div>

      {/* ── Drill Modal ───────────────────────────────────────────────────── */}
      {drill && (
        <div
          className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setDrill(null)}
          data-testid="delegate-drill-modal"
        >
          <div
            className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-emerald-500 to-teal-600">
              <div className="text-white flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-3xl leading-none shrink-0" title={drill.country_code}>
                  {codeToFlag(drill.country_code)}
                </div>
                <div>
                  <h2 className="text-lg font-bold">
                    {tr ? drill.country_tr : drill.country_en}
                  </h2>
                  <p className="text-xs text-white/80 mt-0.5">
                    {tr ? drill.country_en : drill.country_tr} · {drill.country_code}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setDrill(null)}
                className="text-white/80 hover:text-white p-1 rounded-full"
                data-testid="delegate-drill-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Phase 86AT — Remove country from map (Ultra Password gated).
                Lets admins purge stale UNFCCC rows that the test-data wipe
                doesn't catch (the original "stuck Belgium" report). */}
            <div className="px-6 py-2 border-b border-gray-100 flex items-center justify-end bg-rose-50/50">
              <button
                data-testid={`purge-country-btn-${drill.country_code}`}
                onClick={() => setPurgeModal({ country: drill, password: '', loading: false, error: '' })}
                title={tr ? 'Bu ülkeyi haritadan kaldır (Ultra Password gerekli)' : 'Remove this country from the map (Ultra Password required)'}
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-rose-700 hover:text-rose-800 bg-white hover:bg-rose-100 border border-rose-200 rounded-lg px-2.5 py-1 transition-colors"
              >
                <Trash2 className="h-3 w-3" />
                {tr ? 'Haritadan Kaldır' : 'Remove from Map'}
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              {/* Status banner */}
              {drill.unfccc_locked ? (
                <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-2xl p-4">
                  <Shield className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
                  <div className="text-sm">
                    <p className="font-semibold text-emerald-900">
                      {tr ? 'UNFCCC kilidi aktif' : 'UNFCCC lock active'}
                    </p>
                    <p className="text-emerald-700 text-[12px] mt-1">
                      {tr
                        ? 'Bu ülke için Blue Zone self-register akışı kapalıdır. Resmi delege(ler) yalnızca UNFCCC E-posta Doğrulama ile giriş yapabilir.'
                        : 'Blue Zone self-registration is locked for this country. Official delegate(s) can only enter via UNFCCC Email Verification.'}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3 bg-sky-50 border border-sky-200 rounded-2xl p-4">
                  <AlertTriangle className="h-5 w-5 text-sky-600 mt-0.5 shrink-0" />
                  <div className="text-sm">
                    <p className="font-semibold text-sky-900">
                      {tr ? 'UNFCCC kilidi yok' : 'No UNFCCC lock'}
                    </p>
                    <p className="text-sky-700 text-[12px] mt-1">
                      {tr
                        ? 'Bu ülke için henüz UNFCCC delegesi eklenmemiş. Blue Zone self-register açık — UNFCCC listesine e-posta eklerseniz otomatik olarak kilitlenir.'
                        : 'No UNFCCC delegate is registered for this country yet. Blue Zone self-register is open — adding a delegate email will auto-lock it.'}
                    </p>
                  </div>
                </div>
              )}

              {/* UNFCCC emails */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">
                    {tr ? 'UNFCCC Delegeleri' : 'UNFCCC Delegates'}
                    <span className="ml-2 text-gray-500 font-medium normal-case">({drill.unfccc_count})</span>
                  </p>
                  {drill.unfccc_count > 0 && (
                    <button
                      data-testid={`send-welcome-country-${drill.country_code}`}
                      onClick={() => openCompose(
                        (drill.unfccc_emails || []).map(e => ({
                          email: e.email, name: e.name || '', country_code: drill.country_code,
                        })),
                      )}
                      className="flex items-center gap-1.5 text-[11px] font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-2.5 py-1 transition-colors"
                    >
                      <Send className="h-3 w-3" />
                      {tr ? 'Bu ülkeye mail gönder' : 'Email this country'}
                    </button>
                  )}
                </div>
                {drill.unfccc_emails.length === 0 ? (
                  <div className="text-sm text-gray-400 italic bg-gray-50 rounded-xl p-4">
                    {tr ? 'Bu ülke için UNFCCC emaili tanımlanmamış.' : 'No UNFCCC email defined for this country.'}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {drill.unfccc_emails.map(d => (
                      <div
                        key={d.id || d.email}
                        data-testid={`delegate-email-${d.email}`}
                        className="flex items-center justify-between gap-3 bg-gray-50 border border-gray-100 rounded-xl p-3"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-9 w-9 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                            <Mail className="h-4 w-4 text-emerald-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">{d.name || d.email}</p>
                            <p className="text-[11px] text-gray-500 truncate">{d.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            data-testid={`send-welcome-${d.email}`}
                            onClick={() => openCompose([{
                              email: d.email,
                              name:  d.name || '',
                              country_code: drill.country_code,
                            }])}
                            className="p-1.5 rounded-lg border border-emerald-200 text-emerald-600 hover:bg-emerald-50 transition-colors"
                            title={tr ? 'Bu delegeye mail gönder' : 'Email this delegate'}
                          >
                            <Send className="h-3.5 w-3.5" />
                          </button>
                          <span
                            className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                              d.is_used
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-amber-100 text-amber-700'
                            }`}
                          >
                            {d.is_used
                              ? (tr ? 'Kayıt oldu' : 'Registered')
                            : (tr ? 'Bekleniyor' : 'Waiting')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Counts row */}
              <div className="grid grid-cols-3 gap-3 pt-2">
                <div className="bg-white border border-gray-100 rounded-xl p-3 text-center">
                  <p className="text-[10px] uppercase text-gray-400 font-semibold tracking-wide">
                    {tr ? 'Aktif Kullanıcı' : 'Active Users'}
                  </p>
                  <p className="text-xl font-extrabold text-sky-700 mt-1">{drill.registered_count}</p>
                </div>
                <div className="bg-white border border-gray-100 rounded-xl p-3 text-center">
                  <p className="text-[10px] uppercase text-gray-400 font-semibold tracking-wide">
                    {tr ? 'Bekleyen Başvuru' : 'Pending'}
                  </p>
                  <p className="text-xl font-extrabold text-amber-600 mt-1">{drill.pending_count}</p>
                </div>
                <div className="bg-white border border-gray-100 rounded-xl p-3 text-center">
                  <p className="text-[10px] uppercase text-gray-400 font-semibold tracking-wide">
                    {tr ? 'UNFCCC' : 'UNFCCC'}
                  </p>
                  <p className="text-xl font-extrabold text-emerald-700 mt-1">{drill.unfccc_count}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Professional compose modal */}
      <DelegateEmailCompose
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        recipients={composeRecipients}
        tr={tr}
        currentUserEmail={currentUserEmail}
      />

      {/* Phase 86AT — Purge Country modal (Ultra Password) */}
      {purgeModal && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/55 p-4" data-testid="purge-country-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-6 h-6 text-rose-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-800 text-lg">
                  {tr ? 'Ülkeyi Haritadan Kaldır' : 'Remove Country from Map'}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {tr ? 'Bu işlem geri alınamaz · Audit log\'a yazılır' : 'Irreversible · audit-logged'}
                </p>
              </div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3 text-sm text-gray-700">
              <div className="flex items-center gap-2">
                <span className="text-2xl leading-none">{codeToFlag(purgeModal.country?.country_code)}</span>
                <div>
                  <div className="font-bold">{tr ? purgeModal.country?.country_tr : purgeModal.country?.country_en}</div>
                  <div className="text-xs text-gray-500">{purgeModal.country?.country_code}</div>
                </div>
              </div>
              <ul className="text-xs text-gray-500 mt-2 list-disc list-inside space-y-0.5">
                <li>{purgeModal.country?.unfccc_count || 0} {tr ? 'UNFCCC e-postası silinecek' : 'UNFCCC emails will be deleted'}</li>
                <li>{purgeModal.country?.pending_count || 0} {tr ? 'bekleyen Blue Zone başvurusu silinecek' : 'pending Blue Zone registrations will be deleted'}</li>
                <li className="text-emerald-700">{tr ? 'Aktif müşteri hesapları korunur' : 'Live customer accounts are preserved'}</li>
              </ul>
            </div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              {tr ? 'Ultra Password' : 'Ultra Password'}
            </label>
            <input
              type="password"
              autoFocus
              data-testid="purge-country-password"
              value={purgeModal.password || ''}
              onChange={e => setPurgeModal(prev => ({ ...prev, password: e.target.value, error: '' }))}
              onKeyDown={e => { if (e.key === 'Enter') handlePurgeCountry(); }}
              placeholder={tr ? 'Ultra Password girin' : 'Enter Ultra Password'}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300"
            />
            {purgeModal.error && (
              <p className="text-xs text-rose-600 mt-2" data-testid="purge-country-error">{purgeModal.error}</p>
            )}
            <div className="flex gap-3 mt-5">
              <button
                data-testid="purge-country-cancel"
                onClick={() => setPurgeModal(null)}
                disabled={purgeModal.loading}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50">
                {tr ? 'Vazgeç' : 'Cancel'}
              </button>
              <button
                data-testid="purge-country-confirm"
                onClick={handlePurgeCountry}
                disabled={purgeModal.loading || !(purgeModal.password || '').trim()}
                className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold transition-colors disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg,#e11d48,#be123c)' }}>
                {purgeModal.loading ? (tr ? 'Siliniyor…' : 'Removing…') : (tr ? 'Kaldır' : 'Remove')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
